import { useEffect, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import type { DropDownResult } from '@myosh/odin-components';
import { useLazyGetUserByIdQuery } from '../../../redux/services/user';
import type { DefaultPersonConfiguration } from '../../../@types/forms';
import { UserFields } from '../../../utils/user-config';

interface ParentPersonManagerProps {
  control: Control;
  fieldId: number;
  config: DefaultPersonConfiguration;
}

interface TargetFieldValueOriginalData {
  managerId?: number;
  managerText?: string;
  text?: string;
  value?: unknown;
  _uniqueId?: string;
}

interface TargetFieldValue {
  originalData?: TargetFieldValueOriginalData;
  text?: string;
  value?: unknown;
}

interface ManagerValue extends DropDownResult {
  managerId?: number;
  managerText?: string;
}

const useGetPersonFieldDefaultValue = ({ control, config }: ParentPersonManagerProps) => {
  const [manager, setManager] = useState<ManagerValue>();
  const [getUserById] = useLazyGetUserByIdQuery();

  const targetFieldName = String(config?.parentField?.id);
  const isDefaultValueAnotherPersonFieldsManager = config?.defaultValue === '[AnotherPersonFieldsManager]';

  const targetFieldValue: TargetFieldValue | undefined = useWatch({
    control,
    name: targetFieldName,
    disabled: !isDefaultValueAnotherPersonFieldsManager,
    exact: true,
  });

  const { dirtyFields } = useFormState({
    control,
    name: targetFieldName,
  });

  useEffect(() => {
    if (isDefaultValueAnotherPersonFieldsManager) {
      if (targetFieldValue && targetFieldValue.value !== null && dirtyFields[targetFieldName]) {
        const { managerId, managerText } = targetFieldValue?.originalData ?? {};
        if (managerText && managerId) {
          setManager({ value: managerId, text: managerText });
        } else if (Number(targetFieldValue.value)) {
          // get the users' manager if not present in field value
          getUserById({ id: Number(targetFieldValue.value), fields: UserFields.manager }, true)
            .unwrap()
            .then((user) => {
              if (user.manager) {
                setManager({ ...(user.manager as DropDownResult) });
              } else {
                setManager(undefined);
              }
            })
            .catch(() => setManager(undefined));
        }
      } else {
        setManager(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFieldValue?.value, targetFieldValue?.originalData?.managerId, targetFieldValue?.originalData?.managerText]);

  return manager;
};

export default useGetPersonFieldDefaultValue;
